// FlowSync.AI - Trigger Vercel deployment
import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Pages
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import Setup2FAPage from "@/pages/Setup2FAPage";
import OnboardingPage from "@/pages/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import WorkflowsPage from "@/pages/WorkflowsPage";
import WorkflowBuilderPage from "@/pages/WorkflowBuilderPage";
import TemplatesPage from "@/pages/TemplatesPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import ExecutionHistoryDashboard from "@/pages/ExecutionHistoryDashboard";
import Top10IntegrationsPage from "@/pages/Top10IntegrationsPage";
import AIAgentChatPage from "@/pages/AIAgentChatPage";
import FAQPage from "@/pages/FAQPage";
import WikiHowPage from "@/pages/WikiHowPage";
import ErrorMonitorPage from "@/pages/ErrorMonitorPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import ReferralDashboardPage from "@/pages/ReferralDashboardPage";
import SettingsPage from "@/pages/SettingsPage";
import WorkflowGeneratorPage from "@/pages/WorkflowGeneratorPage";

// Employee Admin Pages
import EmployeeLoginPage from "@/pages/EmployeeLoginPage";
import TwoFactorSetupPage from "@/pages/TwoFactorSetupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminLayout from "@/layouts/AdminLayout";
import AdminDashboard from "@/pages/AdminDashboard";
import RoleManagementPage from "@/pages/RoleManagementPage";
import RoleDetailPage from "@/pages/RoleDetailPage";
import EmployeeListPage from "@/pages/EmployeeListPage";
import EmployeeFormPage from "@/pages/EmployeeFormPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import AuditLogPage from "@/pages/admin/AuditLogPage";
import SecurityDashboardPage from "@/pages/admin/SecurityDashboardPage";
import EmployeeActivityPage from "@/pages/admin/EmployeeActivityPage";
import EmployeeProtectedRoute from "@/components/ProtectedRoute";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";

// Enhanced Admin Dashboard Pages
import EnhancedAdminLayout from "@/layouts/EnhancedAdminLayout";
import EnhancedAdminDashboard from "@/pages/admin/EnhancedAdminDashboard";
import EnhancedEmployeeListPage from "@/pages/admin/EnhancedEmployeeListPage";
import EnhancedEmployeeDetailPage from "@/pages/admin/EnhancedEmployeeDetailPage";
import EnhancedRoleManagementPage from "@/pages/admin/EnhancedRoleManagementPage";
import MyProfilePage from "@/pages/admin/MyProfilePage";
import SessionManagementPage from "@/pages/admin/SessionManagementPage";
import SecuritySettingsPage from "@/pages/admin/SecuritySettingsPage";
import InvitationManagementPage from "@/pages/admin/InvitationManagementPage";
import OnboardingFlow from "@/pages/public/OnboardingFlow";
import APIDocumentationPage from "@/pages/admin/APIDocumentationPage";
import PricingManagementPage from "@/pages/admin/PricingManagementPage";
import { AuthProvider as EmployeeAuthProvider } from "@/contexts/AuthContext";
import LeadsPage from "@/pages/LeadsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import TasksPage from "@/pages/TasksPage";
import AutoBooksOnboardingPage from "@/pages/AutoBooksOnboardingPage";
import AutoBooksLibraryPage from "@/pages/AutoBooksLibraryPage";
import AutoSalesOnboardingPage from "@/pages/AutoSalesOnboardingPage";
import AutoSalesLibraryPage from "@/pages/AutoSalesLibraryPage";
import AutoMarketingOnboardingPage from "@/pages/AutoMarketingOnboardingPage";
import AutoMarketingLibraryPage from "@/pages/AutoMarketingLibraryPage";
import AnalyticsDashboardPage from "@/pages/AnalyticsDashboardPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import DocumentIntelligencePage from "@/pages/DocumentIntelligencePage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
        .then(response => {
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/dashboard', { state: { user: response.data } });
        })
        .catch(error => {
          console.error('Auth error:', error);
          navigate('/login');
        });
    } else {
      navigate('/login');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen gradient-hero flex items-center justify-center">
      <div className="text-white text-xl">Authenticating...</div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate('/login');
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/setup-2fa" element={<Setup2FAPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/onboarding/autobooks" element={<ProtectedRoute><AutoBooksOnboardingPage /></ProtectedRoute>} />
      <Route path="/autobooks/library" element={<ProtectedRoute><AutoBooksLibraryPage /></ProtectedRoute>} />
      <Route path="/onboarding/autosales" element={<ProtectedRoute><AutoSalesOnboardingPage /></ProtectedRoute>} />
      <Route path="/autosales/library" element={<ProtectedRoute><AutoSalesLibraryPage /></ProtectedRoute>} />
      <Route path="/onboarding/automarketing" element={<ProtectedRoute><AutoMarketingOnboardingPage /></ProtectedRoute>} />
      <Route path="/automarketing/library" element={<ProtectedRoute><AutoMarketingLibraryPage /></ProtectedRoute>} />
      <Route path="/analytics" element={<ProtectedRoute><AnalyticsDashboardPage /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/workflows" element={<ProtectedRoute><WorkflowsPage /></ProtectedRoute>} />
      <Route path="/workflows/generate" element={<ProtectedRoute><WorkflowGeneratorPage /></ProtectedRoute>} />
      <Route path="/workflows/new" element={<ProtectedRoute><WorkflowBuilderPage /></ProtectedRoute>} />
      <Route path="/workflows/:id" element={<ProtectedRoute><WorkflowBuilderPage /></ProtectedRoute>} />
      <Route path="/templates" element={<ProtectedRoute><TemplatesPage /></ProtectedRoute>} />
      <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><ConnectionsPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><DocumentIntelligencePage /></ProtectedRoute>} />
      <Route path="/executions" element={<ProtectedRoute><ExecutionHistoryDashboard /></ProtectedRoute>} />
      <Route path="/error-monitor" element={<ProtectedRoute><ErrorMonitorPage /></ProtectedRoute>} />
      <Route path="/ai-agents" element={<ProtectedRoute><AIAgentChatPage /></ProtectedRoute>} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/wikihow" element={<ProtectedRoute><WikiHowPage /></ProtectedRoute>} />
      <Route path="/integrations/top10" element={<ProtectedRoute><Top10IntegrationsPage /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsPage /></ProtectedRoute>} />
      <Route path="/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      {/* Employee Admin Routes */}
      <Route path="/admin/login" element={<EmployeeLoginPage />} />
      <Route path="/admin/2fa-setup" element={<TwoFactorSetupPage />} />
      <Route path="/admin/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<ResetPasswordPage />} />
      
      {/* Protected Admin Routes */}
      <Route path="/admin" element={<AdminProtectedRoute><EnhancedAdminLayout /></AdminProtectedRoute>}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<EnhancedAdminDashboard />} />
        <Route path="employees" element={<EnhancedEmployeeListPage />} />
        <Route path="employees/new" element={<EmployeeFormPage />} />
        <Route path="employees/:id" element={<EnhancedEmployeeDetailPage />} />
        <Route path="employees/:id/edit" element={<EmployeeFormPage />} />
        <Route path="roles" element={<EnhancedRoleManagementPage />} />
        <Route path="profile" element={<MyProfilePage />} />
        <Route path="roles/:id" element={<RoleDetailPage />} />
        <Route path="audit-logs" element={<AuditLogPage />} />
        <Route path="security" element={<SecurityDashboardPage />} />
                    <Route path="employees/:employeeId/activity" element={<EmployeeActivityPage />} />
              <Route path="/admin/sessions" element={<SessionManagementPage />} />
        <Route path="security-settings" element={<SecuritySettingsPage />} />
        <Route path="invitations" element={<InvitationManagementPage />} />
        <Route path="api-docs" element={<APIDocumentationPage />} />
        <Route path="pricing" element={<PricingManagementPage />} />
      </Route>
      <Route path="/referrals" element={<ProtectedRoute><ReferralDashboardPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/settings/:tab" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <EmployeeAuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </EmployeeAuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
